/**
 * <WcJoinCard mode="tournament|immersion" accent="#hex" />
 *
 * iter 96 — REAL enrollment card for the two roster modes (Authentic
 * Tournament + Immersion League). Unlike the pre-launch <WcRegisterBanner/>
 * (which only records interest in wc_registrations), this performs the actual
 * join:
 *   • tournament → POST /wctourney/join  → draws a national team + group,
 *     fills a slot, and the player shows in the admin "Registered Players".
 *   • immersion  → POST /wc26/register   → assigns a shard, shows in players.
 *
 * Both joins are gated server-side by the admin "registration" flag, so when
 * the admin closes registration the button is blocked with a clear message.
 * The player picks their platform here (mobile/console are separate leagues).
 */
import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { Loader2, Check, Sparkles, Smartphone, Monitor, ArrowRight } from "lucide-react";

const PLATS = ["mobile", "console"];
const PLAT_LABEL = { mobile: "Mobile", console: "Console" };
const PLAT_ICON = { mobile: Smartphone, console: Monitor };

const CFG = {
  tournament: {
    label: "48-Team Tournament",
    blurb: "Pick your platform to join. You'll be drawn a national team and placed in a group.",
    stateUrl: (p) => `/wctourney/state?platform=${p}`,
    joinUrl: "/wctourney/join",
    joinBody: (p) => ({ platform: p }),
    enrolled: (s) => !!s.in_tournament,
    teamOf: (s) => s.my_team || null,
    groupOf: (s) => s.my_group || null,
  },
  immersion: {
    label: "Immersion League",
    blurb: "Pick your platform to join. You'll be placed into the daily league for that platform.",
    stateUrl: (p) => `/wc26/state?pool=${p}`,
    joinUrl: "/wc26/register",
    joinBody: (p) => ({ pool: p }),
    enrolled: (s) => !!s.registered,
    teamOf: () => null,
    groupOf: () => null,
  },
};

export default function WcJoinCard({ mode, accent = "#FFD700" }) {
  const cfg = CFG[mode] || CFG.tournament;
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);
  const [byPlat, setByPlat] = useState({});

  const loadAll = useCallback(async () => {
    setLoading(true);
    const next = {};
    await Promise.all(PLATS.map(async (p) => {
      try {
        const { data } = await api.get(cfg.stateUrl(p));
        next[p] = { enrolled: cfg.enrolled(data), team: cfg.teamOf(data), group: cfg.groupOf(data) };
      } catch (e) { next[p] = { enrolled: false }; }
    }));
    setByPlat(next);
    setLoading(false);
  }, [cfg]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const join = async (p) => {
    setBusy(p);
    try {
      const { data } = await api.post(cfg.joinUrl, cfg.joinBody(p));
      if (mode === "tournament") {
        const t = data.team;
        if (data.already) {
          toast.success(`You're already in · ${PLAT_LABEL[p]}`);
        } else if (t && t.name) {
          // teams revealed → show the drawn team
          toast.success(`You drew ${t.flag || ""} ${t.name}! Group ${data.group} · ${PLAT_LABEL[p]}`);
        } else {
          // joined, but the team stays hidden until the admin reveals teams
          toast.success(`You're in · ${PLAT_LABEL[p]} — your team is revealed when the admin opens it.`);
        }
      } else {
        toast.success(`You're registered for ${cfg.label} · ${PLAT_LABEL[p]}!`);
      }
      await loadAll();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(null); }
  };

  if (loading) {
    return (
      <div className="rounded-2xl border px-4 py-3 flex items-center justify-center"
           style={{ borderColor: `${accent}33`, background: "rgba(0,0,0,.3)" }}
           data-testid={`wc-join-${mode}-loading`}>
        <Loader2 className="w-4 h-4 animate-spin" style={{ color: accent }} />
      </div>
    );
  }

  const enrolledPlat = PLATS.find((p) => byPlat[p]?.enrolled);

  if (enrolledPlat) {
    const info = byPlat[enrolledPlat] || {};
    return (
      <div className="rounded-2xl border px-4 py-3.5 flex items-center gap-3"
           style={{ borderColor: `${accent}66`, background: `${accent}10` }}
           data-testid={`wc-join-${mode}-enrolled`}>
        <div className="shrink-0 w-9 h-9 rounded-full flex items-center justify-center"
             style={{ background: `${accent}22`, border: `1px solid ${accent}66` }}>
          <Check className="w-4 h-4" style={{ color: accent }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-mono uppercase tracking-[0.25em]" style={{ color: accent }}>
            // You're in · {PLAT_LABEL[enrolledPlat]}
          </div>
          <div className="text-[13px] text-white/85 font-semibold leading-tight">
            {mode === "tournament"
              ? (info.team
                  ? <>Your team: <span className="text-white">{info.team.flag} {info.team.name}</span> · Group {info.group}</>
                  : <>You’re registered — your team is revealed when the admin opens the team reveal.</>)
              : <>You’re registered. Open the league below to roll your daily team and play.</>}
          </div>
        </div>
        <Sparkles className="w-4 h-4 shrink-0" style={{ color: accent }} />
      </div>
    );
  }

  return (
    <div className="rounded-2xl border px-4 py-3.5"
         style={{ borderColor: `${accent}55`, background: `linear-gradient(135deg, ${accent}10 0%, rgba(2,6,23,0.4) 100%)` }}
         data-testid={`wc-join-${mode}`}>
      <div className="text-[10px] font-mono uppercase tracking-[0.25em]" style={{ color: accent }}>// Register &amp; join</div>
      <div className="text-[12.5px] text-white/80 leading-snug mt-0.5 mb-2.5">{cfg.blurb}</div>
      <div className="flex items-center gap-2">
        {PLATS.map((p) => {
          const Icon = PLAT_ICON[p];
          return (
            <button
              key={p}
              type="button"
              onClick={() => join(p)}
              disabled={!!busy}
              data-testid={`wc-join-btn-${mode}-${p}`}
              className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-full text-[11px] font-mono uppercase tracking-widest font-bold transition-colors disabled:opacity-60"
              style={{ background: accent, color: "#020617" }}
            >
              {busy === p ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Icon className="w-3.5 h-3.5" />}
              Join · {PLAT_LABEL[p]} <ArrowRight className="w-3 h-3" />
            </button>
          );
        })}
      </div>
    </div>
  );
}
